import { Banner, Box, Button, IconButton } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import classnames from 'classnames';
import type {
  NotificationActionIteratorT,
  NotificationT,
} from 'lib-shared-legacy-types';
import MomentTimezone from 'moment-timezone';
import * as React from 'react';
import type { Action } from 'redux';

import { actions as conversationActions } from 'components/conversation/actions/conversation';
import { RouteComponentProps, withRouter } from 'hocs/router';

import { NotificationLayout } from './notificationLayout';
import styles from './notificationToaster.module.scss';

export const { conversationExtendAcw } = conversationActions;

type OwnProps = {
  className?: string;
  notification: NotificationT;
  dispatch: (action: Action) => void;
  notificationRemoveById: (payload: { id: string }) => void;
};

type NotificationToasterImplProps = OwnProps & RouteComponentProps;

type StateT = {
  timeLeft: number | null;
  progress: number | null;
};

function timerFormat(timeLeftInMilisec: number) {
  let time = timeLeftInMilisec / 1000;
  let day = '';
  const days = Math.floor(time / 86400); // Days in int.
  if (days > 0) {
    day = days === 1 ? `${days} day ` : `${days} days `;
  }
  let format = 's';
  const hours = Math.floor(time / 3600);
  time = time - hours * 3600;
  const hoursFormat = hours > 0 ? `${hours}:` : '';
  const minutes = Math.floor(time / 60);
  const minutesFormat = minutes > 0 ? `${minutes}:` : '';

  if (minutesFormat) {
    format = 'mm:ss';
  }

  if (hoursFormat) {
    format = 'HH:mm:ss';
  }

  const timeLeft = MomentTimezone(timeLeftInMilisec).utc().format(format);

  return `${day}${timeLeft}`;
}

class NotificationToasterImpl extends React.PureComponent<
  NotificationToasterImplProps,
  StateT
> {
  intervalId?: NodeJS.Timeout;

  hasTimer(notification: NotificationT) {
    return notification.startTime !== null && notification.startTime > 0;
  }

  hasLeftAction(notification: NotificationT) {
    return !!notification.leftAction && !notification.leftAction.hidden;
  }

  hasRightAction(notification: NotificationT) {
    return !!notification.rightAction && !notification.rightAction.hidden;
  }

  hasAction(notification: NotificationT) {
    return (
      this.hasLeftAction(notification) || this.hasRightAction(notification)
    );
  }

  getNewStateFromStartTime(startTime: NotificationT['startTime']) {
    return {
      timeLeft: startTime ? startTime : null,
      progress: startTime && startTime > 0 ? 100 : null,
    };
  }

  constructor(props: NotificationToasterImplProps) {
    super(props);
    const {
      notification: { startTime },
    } = props;

    if (startTime) {
      this.intervalId = global.setInterval(this.setProgress, 100);
    }

    this.state = this.getNewStateFromStartTime(startTime);
  }

  componentWillUnmount() {
    // Remove timer setInterval.
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  componentDidUpdate(prevProps: NotificationToasterImplProps) {
    const {
      notification: { shouldBeRemovedAt: prevShouldBeRemovedAt },
    } = prevProps;
    const {
      notification: { startTime, shouldBeRemovedAt },
    } = this.props;

    if (prevShouldBeRemovedAt !== shouldBeRemovedAt) {
      // We need to clear the timer setInterval.
      if (this.intervalId) {
        clearInterval(this.intervalId);
      }
      // We update the state with the new timeLeft and progress.
      this.setState(this.getNewStateFromStartTime(startTime), () => {
        if (startTime) {
          // When new state is set we start a new timer.
          this.intervalId = global.setInterval(this.setProgress, 100);
        }
      });
    }
  }

  @boundMethod
  setProgress() {
    const { notification } = this.props;
    if (this.hasTimer(notification)) {
      const nowUnixTime = new Date().getTime();
      const { startTime: timerRunTime, shouldBeRemovedAt: endTime } =
        notification as NotificationT & {
          startTime: number;
          shouldBeRemovedAt: number;
        };
      const startTime = endTime - timerRunTime;
      const progressCountDown =
        100 - (100 / (endTime - startTime)) * (nowUnixTime - startTime);
      const progressNormalized = Math.min(100, Math.max(progressCountDown, 0));
      const timeLeft = Math.round(endTime - nowUnixTime);

      if (progressNormalized === 0) {
        this.timerFinished();
      } else {
        this.setState({ timeLeft, progress: progressNormalized });
      }
    }
  }

  @boundMethod
  timerFinished() {
    const { notification, dispatch } = this.props;

    // Remove notification from reducer and remove the setInterval from here
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }

    this.setState({ timeLeft: 0, progress: 0 });
    if (typeof notification.actionOnTimeout === 'function') {
      notification.actionOnTimeout(dispatch);
    }

    this.props.notificationRemoveById({ id: notification.id });
  }

  getProgress(notification: NotificationT): React.CSSProperties {
    if (this.hasTimer(notification)) {
      return { width: this.state.progress + '%' };
    }

    return {};
  }

  handleActionButton(
    notification: NotificationT,
    side: 'leftAction' | 'rightAction',
  ) {
    const buttonSide = notification[side];
    if (buttonSide?.action) {
      const action = buttonSide.action;

      if (typeof action === 'function') {
        this.nextAndExecute(action());
      } else if (typeof action?.navigate === 'string') {
        this.props.navigate(action.navigate);
      }
    }

    return;
  }

  @boundMethod
  handleActionButtonLeft() {
    const { notification } = this.props;
    this.handleActionButton(notification, 'leftAction');
  }

  @boundMethod
  handleActionButtonRight() {
    const { notification } = this.props;
    this.handleActionButton(notification, 'rightAction');
  }

  @boundMethod
  nextAndExecute(action: null | void | NotificationActionIteratorT) {
    // abort if we have not passed an iterator
    if (!action || !(typeof action?.next === 'function')) return;

    const next = action.next();
    if (next.value) {
      const nextCopy = Object.assign({}, next);
      const { timeLeft } = this.state;

      // We add remaining duration to the extended time.
      const nextAction = nextCopy.value?.payload?.action;
      if (nextAction?.type === conversationExtendAcw.toString()) {
        nextAction.payload.duration = Math.round(
          nextAction.payload.duration + (timeLeft as number) / 1000,
        );
      }
      if (nextAction) {
        this.props.dispatch(nextAction);
      }

      this.nextAndExecute(action);
    }
  }

  @boundMethod
  handleClose() {
    const { notification } = this.props;
    if (typeof notification?.closeAction === 'function') {
      this.nextAndExecute(notification.closeAction());
    } else if (notification?.id) {
      this.props.notificationRemoveById({ id: notification.id });
    }
  }

  getRightButtonText() {
    const { notification } = this.props;

    const base = notification.rightAction?.text ?? '';

    const timeLeft =
      typeof this.state.timeLeft === 'number'
        ? timerFormat(this.state.timeLeft)
        : '';

    return `${base} ${timeLeft}`;
  }

  render() {
    const { notification, className } = this.props;
    const { status } = notification;

    const rootClass = classnames(
      {
        [styles.root]: styles.root,
        [styles.rootSuccess]: status === 'success',
        [styles.rootWarning]: status === 'warning',
        [styles.rootDanger]: status === 'danger',
      },
      className,
    );

    return (
      <Box className={rootClass}>
        {!notification.hideCloseAction && (
          <IconButton
            icon="close"
            variant="tertiary"
            className={styles.close}
            onClick={this.handleClose}
          />
        )}

        <NotificationLayout notification={notification} />

        {notification.banner && (
          <Box margin="300 0 0 0">
            <Banner {...notification.banner} />
          </Box>
        )}

        {this.hasTimer(notification) && (
          <Box className={styles.progress}>
            <Box
              className={styles.progressBar}
              style={this.getProgress(notification)}
            ></Box>
          </Box>
        )}

        {this.hasAction(notification) && (
          <Box className={styles.actions}>
            {this.hasLeftAction(notification) && (
              <Button
                variant="secondary"
                onClick={this.handleActionButtonLeft}
                className={styles.action}
                disabled={notification.leftAction?.disabled}
              >
                {notification.leftAction ? notification.leftAction.text : ''}
              </Button>
            )}
            {this.hasRightAction(notification) && (
              <Button
                variant="secondary"
                onClick={this.handleActionButtonRight}
                className={styles.action}
                disabled={notification.rightAction?.disabled}
              >
                {this.getRightButtonText()}
              </Button>
            )}
          </Box>
        )}
      </Box>
    );
  }
}

export const NotificationToaster = withRouter(NotificationToasterImpl);
